import type {ReactElement} from 'react'
import {addMaybeClassName} from '@freckle/educator-entities/ts/common/helpers/classnames'
import {Button} from '@freckle/educator-materials/ts/components/button'
import type {ButtonStyleT} from '@freckle/educator-materials/ts/components/button'
import {Link} from '@freckle/educator-materials/ts/components/link'
import {Modal, Body} from '@freckle/educator-materials/ts/components/modal'
import {H3} from '@freckle/educator-materials/ts/components/typography'
import type {SizeT} from '@freckle/educator-materials/ts/helpers/size'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {useBoolean} from '@freckle/classroom/ts/hooks/use-boolean'
import {footer, linkButton} from './external-link-with-confirmation.module.scss'

type Props = {
  href: string
  show?: boolean
  target?: string
  children?: ReactElement
  onHide?: () => void
  // Button props
  onClick?: () => void
  size?: SizeT
  className?: string
  style?: ButtonStyleT
  dataTest?: string
}

export const ExternalLinkWithConfirmation = (props: Props): ReactElement => {
  const {t} = useSafeTranslation()
  const {
    value: show,
    setTrue: setShowToTrue,
    setFalse: setShowToFalse
  } = useBoolean(props.show ?? false)

  const {href, children, target, onHide, show: _show, onClick: _onClick, ...otherProps} = props
  const buttonProps = {...otherProps, style: otherProps.style ?? 'link'}

  const buttonPropsWithClass =
    buttonProps.style === 'link'
      ? {...buttonProps, className: addMaybeClassName(linkButton, buttonProps.className)}
      : buttonProps

  const ctaButton =
    children !== undefined ? (
      <Button onClick={setShowToTrue} {...buttonPropsWithClass}>
        {children}
      </Button>
    ) : null

  // allows the parent component to take control
  const onHideHandler = onHide ?? setShowToFalse

  const modalNode = show ? (
    <Modal show={show} onHide={onHideHandler} height="small" customStyle={{height: '16vh'}}>
      <Body>
        <H3>{t('EXTERNAL_LINK_WITH_CONFIRMATION_HEADER')}</H3>
        {t('EXTERNAL_LINK_WITH_CONFIRMATION_ARE_YOU_SURE_MESSAGE')}
        <div className={footer}>
          <Button style="tertiary" onClick={onHideHandler}>
            {t('EXTERNAL_LINK_WITH_CONFIRMATION_CANCEL_BUTTON')}
          </Button>
          <Link
            onClick={onHideHandler}
            style="btn-primary"
            href={href}
            dataTest="external-link-confirm-modal-ok"
            target={target}
            rel="noopener"
            reactRouter={false}
          >
            {t('EXTERNAL_LINK_WITH_CONFIRMATION_OK_BUTTON')}
          </Link>
        </div>
      </Body>
    </Modal>
  ) : null

  return (
    <>
      {ctaButton}
      {modalNode}
    </>
  )
}
